import React, { useMemo } from 'react'

import Button from 'components/Form/Button'
import FormCore from 'components/Form/Core'
import { FormScreenSkeleton } from 'components/Skeleton'

import useController from './controller'
import {
  ActionRow,
  ConfirmationPanel,
  DecisionCopy,
  DetailCard,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  ErrorState,
  Header,
  OutcomeBanner,
  Page,
  ReviewSection,
  SectionHeading,
  StatusBadge,
  Subtitle,
  Title,
} from './styled'

function formatDate(value) {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString()
}

export default function TrainingApprovalReview() {
  const {
    approval,
    loading,
    error,
    action,
    decision,
    feedback,
    rejectionFormRef,
    retry,
    onBack,
    onApprove,
    onReject,
    onCancelDecision,
    onConfirmDecision,
  } = useController()
  const rejectionItems = useMemo(() => ([{
    ref: 'reviewNote',
    label: 'Review note (optional)',
    placeholder: 'Add context for the participant',
    type: 'textarea',
    full: true,
  }]), [])
  const pending = ['pending', 'pending_trainer_approval'].includes(approval?.status)
  const statusLabel = pending ? 'Pending approval' : approval?.status === 'approved' ? 'Approved' : approval?.status === 'rejected' ? 'Rejected' : approval?.status

  return (
    <Page>
      {loading ? (
        <FormScreenSkeleton />
      ) : error ? (
        <ErrorState>
          <Title>Certification review unavailable</Title>
          <Subtitle>{error}</Subtitle>
          <ActionRow>
            <Button outline color="blue" nospace width="fit-content" onClick={onBack}>Back</Button>
            <Button color="blue" nospace width="fit-content" onClick={retry}>Try again</Button>
          </ActionRow>
        </ErrorState>
      ) : (
        <>
          <Header>
            <div>
              <Subtitle>Certification approval</Subtitle>
              <Title>Certification Review</Title>
            </div>
            <StatusBadge status={approval?.status}>{statusLabel}</StatusBadge>
          </Header>
          <DetailCard>
            <ReviewSection>
              <SectionHeading>Participant</SectionHeading>
              <DetailGrid>
              <DetailItem><DetailLabel>Name</DetailLabel><DetailValue>{approval?.user?.name || '—'}</DetailValue></DetailItem>
              <DetailItem><DetailLabel>Email</DetailLabel><DetailValue>{approval?.user?.email || '—'}</DetailValue></DetailItem>
              </DetailGrid>
            </ReviewSection>
            <ReviewSection>
              <SectionHeading>Training</SectionHeading>
              <DetailGrid>
              <DetailItem><DetailLabel>Certification</DetailLabel><DetailValue>{approval?.course?.title || '—'}</DetailValue></DetailItem>
              <DetailItem><DetailLabel>Training module</DetailLabel><DetailValue>{approval?.courseModule?.title || '—'}</DetailValue></DetailItem>
              </DetailGrid>
            </ReviewSection>
            <ReviewSection>
              <SectionHeading>Quiz Result</SectionHeading>
              <DetailGrid>
              <DetailItem><DetailLabel>Quiz</DetailLabel><DetailValue>{approval?.quiz?.title || '—'}</DetailValue></DetailItem>
              <DetailItem><DetailLabel>Score</DetailLabel><DetailValue>{approval?.quizScore === null ? '—' : `${Math.round(Number(approval?.quizScore))}%`}</DetailValue></DetailItem>
              </DetailGrid>
            </ReviewSection>
            <ReviewSection>
              <SectionHeading>Completion</SectionHeading>
              <DetailGrid>
              <DetailItem><DetailLabel>Completed</DetailLabel><DetailValue>{formatDate(approval?.requestedAt)}</DetailValue></DetailItem>
              {!approval?.reviewedAt ? null : <DetailItem><DetailLabel>Reviewed</DetailLabel><DetailValue>{formatDate(approval.reviewedAt)}</DetailValue></DetailItem>}
              </DetailGrid>
            </ReviewSection>
            <ReviewSection>
              <SectionHeading>Trainer</SectionHeading>
              <DetailGrid>
              <DetailItem><DetailLabel>Trainer</DetailLabel><DetailValue>{approval?.trainer?.name || '—'}</DetailValue></DetailItem>
              {!approval?.reviewNote ? null : <DetailItem><DetailLabel>Review note</DetailLabel><DetailValue>{approval.reviewNote}</DetailValue></DetailItem>}
              </DetailGrid>
            </ReviewSection>
            <ReviewSection>
              <SectionHeading>Decision</SectionHeading>
              {!pending ? (
                <OutcomeBanner status={approval?.status || feedback}>
                  <strong>{approval?.status === 'approved' ? 'Certification approved' : 'Certification rejected'}</strong>
                  <span>{approval?.status === 'approved'
                    ? "The participant's certificate has been issued successfully."
                    : 'No certificate was issued. This decision remains available in the approval history.'}</span>
                </OutcomeBanner>
              ) : (
                <>
                  <DecisionCopy>Confirm the participant’s training result, then approve the certificate or reject the request with an optional note.</DecisionCopy>
                  <FormCore ref={rejectionFormRef} formItems={rejectionItems} nospace />
                  {!decision ? (
                    <ActionRow>
                      <Button outline color="blue" nospace width="fit-content" onClick={onBack}>Back to Approvals</Button>
                      <Button color="error" disabled={!!action} nospace width="fit-content" onClick={onReject}>Reject</Button>
                      <Button color="success" disabled={!!action || !approval?.requirements?.eligible} nospace width="fit-content" onClick={onApprove}>Approve Certification</Button>
                    </ActionRow>
                  ) : (
                    <ConfirmationPanel status={decision}>
                      <strong>{decision === 'approve' ? 'Approve this certification?' : 'Reject this certification request?'}</strong>
                      <span>{decision === 'approve'
                        ? 'The certificate will be issued immediately and made available to the participant.'
                        : 'No certificate will be issued. The participant will be notified of the decision.'}</span>
                      <ActionRow>
                        <Button outline color="blue" disabled={!!action} nospace width="fit-content" onClick={onCancelDecision}>Cancel</Button>
                        <Button
                          color={decision === 'approve' ? 'success' : 'error'}
                          disabled={!!action}
                          loading={action === decision}
                          nospace
                          width="fit-content"
                          onClick={onConfirmDecision}
                        >
                          {decision === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
                        </Button>
                      </ActionRow>
                    </ConfirmationPanel>
                  )}
                </>
              )}
              {!pending ? (
                <ActionRow><Button outline color="blue" nospace width="fit-content" onClick={onBack}>Back to Approvals</Button></ActionRow>
              ) : null}
            </ReviewSection>
          </DetailCard>
        </>
      )}
    </Page>
  )
}
